import { billingModelLabel } from './format';
import { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { useOrg } from './orgContext';

// TimeKeep HQ — the platform operator's own console. Not org-branded (there is no
// active org for this identity), and never reachable by any customer/org account.
// Gated purely by is_platform_admin() — see src/App.js.
const C = {
  bg: '#0A1526',
  surface: '#16233F',
  border: '#28395C',
  accent: '#00A0C8',
  text: '#E2E8F0',
  muted: '#64748b',
};

const NAV_ITEMS = [
  { key: 'overview', label: 'Overview' },
  { key: 'orgs', label: 'Organizations' },
  { key: 'sandbox', label: 'Sandbox' },
  { key: 'feedback', label: 'Feedback' },
  { key: 'audit', label: 'Audit Log' },
  { key: 'tools', label: 'Tools' },
];

function StatTile({ label, value }) {
  return (
    <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:'16px 20px', minWidth:160 }}>
      <div style={{ fontSize:12, color:C.muted, fontWeight:600, textTransform:'uppercase', letterSpacing:0.4 }}>{label}</div>
      <div style={{ fontSize:26, fontWeight:700, color:C.text, marginTop:6 }}>{value}</div>
    </div>
  );
}

function Badge({ children, tone = 'muted' }) {
  const tones = {
    muted:  { bg: `${C.muted}22`, fg: C.muted },
    accent: { bg: `${C.accent}22`, fg: C.accent },
    warn:   { bg: '#f8717122', fg: '#f87171' },
    good:   { bg: '#4ade8022', fg: '#4ade80' },
    bad:    { bg: '#f8717122', fg: '#f87171' },
  };
  const t = tones[tone] || tones.muted;
  return (
    <span style={{ background:t.bg, color:t.fg, fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:999, textTransform:'uppercase', letterSpacing:0.3 }}>
      {children}
    </span>
  );
}

const SECTIONS = ['members', 'clients', 'invoices'];

function OrgDetailPanel({ org, onClose }) {
  const [activeSection, setActiveSection] = useState('members');
  const [sectionData, setSectionData] = useState({});
  const [loadingSection, setLoadingSection] = useState(false);

  const loadSection = async (section) => {
    setActiveSection(section);
    if (sectionData[section]) return;
    setLoadingSection(true);
    const { data, error } = await supabase.functions.invoke('platform-org-detail', {
      body: { org_id: org.org_id, section },
    });
    setLoadingSection(false);
    if (!error && data?.ok) setSectionData(prev => ({ ...prev, [section]: data.data }));
  };

  useEffect(() => { loadSection('members'); /* eslint-disable-next-line */ }, [org.org_id]);

  const rows = sectionData[activeSection];

  return (
    <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:20, minWidth:340, maxWidth:420 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <div style={{ fontWeight:700, fontSize:16 }}>{org.name}</div>
          <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>{org.admin_email || '—'}</div>
        </div>
        <button onClick={onClose} style={{ background:'none', border:'none', color:C.muted, cursor:'pointer', fontSize:18, lineHeight:1 }}>×</button>
      </div>
      <div style={{ display:'flex', gap:6, marginTop:10, flexWrap:'wrap' }}>
        <Badge tone={org.account_type === 'organization' ? 'accent' : 'muted'}>{org.account_type}</Badge>
        {!org.setup_complete && <Badge tone="warn">Setup Incomplete</Badge>}
        {org.is_test && <Badge tone="warn">Test/Demo</Badge>}
      </div>

      <div style={{ display:'flex', gap:4, marginTop:16, borderBottom:`1px solid ${C.border}`, paddingBottom:8 }}>
        {SECTIONS.map(s => (
          <button key={s} onClick={() => loadSection(s)}
            style={{
              padding:'5px 12px', borderRadius:7, border:'none', cursor:'pointer', fontSize:12, fontWeight:600,
              background: activeSection === s ? `${C.accent}22` : 'transparent',
              color: activeSection === s ? C.accent : C.muted, textTransform:'capitalize',
            }}>
            {s}
          </button>
        ))}
      </div>

      <div style={{ marginTop:12, fontSize:13 }}>
        {loadingSection && <div style={{ color:C.muted }}>Loading…</div>}
        {!loadingSection && rows && rows.length === 0 && <div style={{ color:C.muted }}>No {activeSection}.</div>}
        {!loadingSection && rows && rows.length > 0 && (
          <div style={{ display:'flex', flexDirection:'column', gap:8, maxHeight:360, overflowY:'auto' }}>
            {rows.map((r, i) => (
              <div key={r.id || i} style={{ borderBottom:`1px solid ${C.border}`, paddingBottom:8 }}>
                {activeSection === 'members' && (
                  <div>
                    <div style={{ fontWeight:600 }}>{r.profiles?.name || '(unnamed)'}</div>
                    <div style={{ color:C.muted, fontSize:12 }}>{r.role} · {r.seat_type} · comp ${r.compensation_rate ?? 0}</div>
                  </div>
                )}
                {activeSection === 'clients' && (
                  <div>
                    <div style={{ fontWeight:600 }}>{r.name} {!r.is_active && <Badge tone="warn">Archived</Badge>}</div>
                    <div style={{ color:C.muted, fontSize:12 }}>{billingModelLabel(r.model)} · rate ${r.rate}</div>
                  </div>
                )}
                {activeSection === 'invoices' && (
                  <div>
                    <div style={{ fontWeight:600 }}>{r.invoice_number} <Badge tone={r.status === 'paid' ? 'accent' : 'muted'}>{r.status}</Badge></div>
                    <div style={{ color:C.muted, fontSize:12 }}>{r.period} · ${Number(r.total).toLocaleString()}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ActiveSupportSessionsPanel({ onExited }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [exitingKey, setExitingKey] = useState(null);

  const load = () => {
    supabase.rpc('platform_admin_active_support_sessions').then(({ data, error }) => {
      if (error) setError(error.message);
      else setRows(data || []);
    });
  };
  useEffect(load, []);

  const forceExit = async (org_id, user_id) => {
    const key = `${org_id}:${user_id}`;
    setExitingKey(key);
    const { error } = await supabase.rpc('platform_admin_force_exit_org', { p_org_id: org_id, p_user_id: user_id });
    setExitingKey(null);
    if (!error) { load(); onExited?.(); }
  };

  if (error || !rows || rows.length === 0) return null;

  return (
    <div style={{ background:'rgba(239,68,68,0.10)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:10, padding:'14px 18px', marginBottom:16 }}>
      <div style={{ fontSize:12, fontWeight:700, color:'#fca5a5', textTransform:'uppercase', letterSpacing:0.4, marginBottom:8 }}>
        Active Support Sessions
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
        {rows.map(r => (
          <div key={`${r.org_id}:${r.user_id}`} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:13 }}>
            <span>{r.admin_email} → <strong>{r.org_name}</strong> · since {new Date(r.started_at).toLocaleString()}</span>
            <button onClick={() => forceExit(r.org_id, r.user_id)} disabled={exitingKey === `${r.org_id}:${r.user_id}`}
              style={{ padding:'4px 10px', borderRadius:6, border:'1px solid rgba(252,165,165,0.5)', background:'transparent', color:'#fca5a5', fontSize:11, fontWeight:600, cursor:'pointer' }}>
              Force Exit
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrganizationsTab() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [hideTest, setHideTest] = useState(true);
  const [selectedOrgId, setSelectedOrgId] = useState(null);

  useEffect(() => {
    (async () => {
      const [summaryRes, orgsRes] = await Promise.all([
        supabase.rpc('platform_admin_org_summary'),
        supabase.from('organizations').select('id, admin_email'),
      ]);
      if (summaryRes.error) { setError(summaryRes.error.message); return; }
      const emailById = Object.fromEntries((orgsRes.data || []).map(o => [o.id, o.admin_email]));
      setRows((summaryRes.data || []).map(r => ({ ...r, name: r.org_name, admin_email: emailById[r.org_id] })));
    })();
  }, []);

  if (error) return <div style={{ color:'#f87171' }}>Failed to load: {error}</div>;
  if (!rows) return <div style={{ color:C.muted }}>Loading…</div>;

  const filtered = rows.filter(r => {
    if (hideTest && r.is_test) return false;
    if (typeFilter !== 'all' && r.account_type !== typeFilter) return false;
    if (search && !`${r.name} ${r.admin_email || ''}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const selected = filtered.find(r => r.org_id === selectedOrgId) || rows.find(r => r.org_id === selectedOrgId);

  return (
    <div style={{ display:'flex', gap:20, alignItems:'flex-start' }}>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
          <input placeholder="Search name or admin email…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ padding:'7px 10px', borderRadius:7, border:`1px solid ${C.border}`, background:C.surface, color:C.text, fontSize:13, minWidth:220 }} />
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            style={{ padding:'7px 10px', borderRadius:7, border:`1px solid ${C.border}`, background:C.surface, color:C.text, fontSize:13 }}>
            <option value="all">All Types</option>
            <option value="organization">Organization</option>
            <option value="individual">Individual</option>
          </select>
          <label style={{ fontSize:13, color:C.muted, display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
            <input type="checkbox" checked={hideTest} onChange={e => setHideTest(e.target.checked)} />
            Hide test orgs
          </label>
          <span style={{ fontSize:12, color:C.muted, marginLeft:'auto' }}>{filtered.length} of {rows.length}</span>
        </div>
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ textAlign:'left', color:C.muted, fontSize:11, textTransform:'uppercase' }}>
                <th style={{ padding:'10px 14px' }}>Org</th>
                <th style={{ padding:'10px 14px' }}>Type</th>
                <th style={{ padding:'10px 14px' }}>Members</th>
                <th style={{ padding:'10px 14px' }}>Active Clients</th>
                <th style={{ padding:'10px 14px' }}>Paid</th>
                <th style={{ padding:'10px 14px' }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.org_id} onClick={() => setSelectedOrgId(r.org_id)}
                  style={{ borderTop:`1px solid ${C.border}`, cursor:'pointer', background: selectedOrgId === r.org_id ? `${C.accent}11` : 'transparent' }}>
                  <td style={{ padding:'10px 14px', fontWeight:600 }}>{r.name}{r.is_test && <span style={{ marginLeft:6 }}><Badge tone="warn">test</Badge></span>}</td>
                  <td style={{ padding:'10px 14px', color:C.muted }}>{r.account_type}</td>
                  <td style={{ padding:'10px 14px' }}>{r.member_count}</td>
                  <td style={{ padding:'10px 14px' }}>{r.active_client_count}</td>
                  <td style={{ padding:'10px 14px' }}>${Number(r.paid_total).toLocaleString()}</td>
                  <td style={{ padding:'10px 14px', color:C.muted }}>{new Date(r.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {selected && <OrgDetailPanel org={selected} onClose={() => setSelectedOrgId(null)} />}
    </div>
  );
}

function SandboxTab() {
  const { reload: reloadOrgs } = useOrg();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [entering, setEntering] = useState(null);
  const [sessionsKey, setSessionsKey] = useState(0);

  useEffect(() => {
    supabase.rpc('platform_admin_org_summary').then(({ data, error }) => {
      if (error) { setError(error.message); return; }
      setRows((data || []).filter(r => r.is_test).map(r => ({ ...r, name: r.org_name })));
    });
  }, []);

  const enterSupport = async (org_id) => {
    setEntering(org_id);
    const { error } = await supabase.rpc('platform_admin_enter_org', { p_org_id: org_id });
    setEntering(null);
    if (error) { window.alert(`Could not enter org: ${error.message}`); return; }
    await reloadOrgs();
  };

  if (error) return <div style={{ color:'#f87171' }}>Failed to load: {error}</div>;
  if (!rows) return <div style={{ color:C.muted }}>Loading…</div>;

  return (
    <div>
      <ActiveSupportSessionsPanel key={sessionsKey} onExited={() => setSessionsKey(k => k + 1)} />
      <div style={{ fontSize:13, color:C.muted, marginBottom:16, maxWidth:640 }}>
        Test and internal organizations only — never real customer data. Safe to enter, modify,
        and experiment with. Entering logs an audit entry and drops you into that org's normal
        Dashboard, where you have full admin access including "View As" for its team members.
      </div>
      {rows.length === 0 ? (
        <div style={{ color:C.muted }}>No test/sandbox organizations found.</div>
      ) : (
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ textAlign:'left', color:C.muted, fontSize:11, textTransform:'uppercase' }}>
                <th style={{ padding:'10px 14px' }}>Org</th>
                <th style={{ padding:'10px 14px' }}>Type</th>
                <th style={{ padding:'10px 14px' }}>Members</th>
                <th style={{ padding:'10px 14px' }}>Active Clients</th>
                <th style={{ padding:'10px 14px' }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.org_id} style={{ borderTop:`1px solid ${C.border}` }}>
                  <td style={{ padding:'10px 14px', fontWeight:600 }}>{r.name}</td>
                  <td style={{ padding:'10px 14px', color:C.muted }}>{r.account_type}</td>
                  <td style={{ padding:'10px 14px' }}>{r.member_count}</td>
                  <td style={{ padding:'10px 14px' }}>{r.active_client_count}</td>
                  <td style={{ padding:'10px 14px' }}>
                    <button onClick={() => enterSupport(r.org_id)} disabled={entering === r.org_id}
                      style={{ padding:'5px 12px', borderRadius:6, border:`1px solid ${C.accent}`, background:'transparent', color:C.accent, fontSize:11, fontWeight:700, cursor:'pointer' }}>
                      {entering === r.org_id ? 'Entering…' : 'Enter Support Session'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AuditLogTab() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    supabase.from('platform_audit_log')
      .select('id, action, target_org_name_snapshot, created_at')
      .order('created_at', { ascending: false })
      .limit(200)
      .then(({ data, error }) => {
        if (error) setError(error.message);
        else setRows(data || []);
      });
  }, []);

  if (error) return <div style={{ color:'#f87171' }}>Failed to load: {error}</div>;
  if (!rows) return <div style={{ color:C.muted }}>Loading…</div>;
  if (rows.length === 0) return <div style={{ color:C.muted }}>No platform-admin activity logged yet.</div>;

  return (
    <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden' }}>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
        <thead>
          <tr style={{ textAlign:'left', color:C.muted, fontSize:11, textTransform:'uppercase' }}>
            <th style={{ padding:'10px 14px' }}>When</th>
            <th style={{ padding:'10px 14px' }}>Action</th>
            <th style={{ padding:'10px 14px' }}>Org</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} style={{ borderTop:`1px solid ${C.border}` }}>
              <td style={{ padding:'10px 14px', color:C.muted }}>{new Date(r.created_at).toLocaleString()}</td>
              <td style={{ padding:'10px 14px' }}>{r.action}</td>
              <td style={{ padding:'10px 14px' }}>{r.target_org_name_snapshot || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const FEEDBACK_TYPES = ['bug', 'feature', 'general'];
// Mirrors FB_AREAS in Dashboard.js — same taxonomy, kept in sync by hand
// since the two files don't share a module.
const FEEDBACK_AREAS = [
  'Timer', 'Time Logs', 'Expenses', 'Client Reports', 'Invoices',
  'Member Invoices', 'Team', 'My Business', 'Settings',
  'Chrome Extension', 'Mobile App', 'Integrations', 'Something else / not sure',
];

const FB_STATUS_LABEL = { new:'New', planned:'Planned', in_progress:'In Progress', shipped:'Shipped', declined:'Declined' };
const FB_STATUS_TONE  = { new:'muted', planned:'accent', in_progress:'warn', shipped:'good', declined:'bad' };

// Mirrors the DB trigger exactly -- offering only valid next states here is a
// UX convenience, not the enforcement; the trigger is what actually protects
// the data if this ever drifts out of sync with it.
const FB_NEXT_STATES = {
  new:         ['planned','declined'],
  planned:     ['in_progress','shipped','declined'],
  in_progress: ['shipped','declined'],
  declined:    ['planned'],
  shipped:     ['planned'],
};

function FeedbackDetailPanel({ item, allItems, onClose, onRespond, onDeleteComment, onMerge }) {
  const [responseText, setResponseText] = useState(item.admin_response || '');
  const [nextStatus, setNextStatus] = useState(item.status);
  const [internalNote, setInternalNote] = useState('');
  const [notes, setNotes] = useState([]);
  const [comments, setComments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  const [mergeQuery, setMergeQuery] = useState('');

  useEffect(() => {
    setResponseText(item.admin_response || ''); setNextStatus(item.status);
    setInternalNote(''); setSaved(false); setSaveErr(null); setMergeQuery('');
    supabase.from('feedback_internal_notes').select('*, profiles(name)').eq('feedback_id', item.id)
      .order('created_at', { ascending:true }).then(({ data }) => setNotes(data||[]));
    supabase.from('feedback_comments').select('*, profiles(name)').eq('feedback_id', item.id)
      .order('created_at', { ascending:true }).then(({ data }) => setComments(data||[]));
  }, [item.id]); // eslint-disable-line

  const statusChanged = nextStatus !== item.status;
  const responseChanged = responseText.trim() !== (item.admin_response || '');
  const needsResponseWarning = (nextStatus==='declined' || nextStatus==='shipped') && !responseText.trim();

  const save = async () => {
    setSaving(true); setSaveErr(null);
    const err = await onRespond(item.id, nextStatus, responseText.trim());
    setSaving(false);
    if (!err) { setSaved(true); setTimeout(() => setSaved(false), 2500); }
    else setSaveErr(err);
  };

  const postNote = async () => {
    if (!internalNote.trim()) return;
    const { data, error } = await supabase.from('feedback_internal_notes')
      .insert({ feedback_id: item.id, note: internalNote.trim() }).select('*, profiles(name)').single();
    if (!error) { setNotes(n => [...n, data]); setInternalNote(''); }
  };

  const mergeCandidates = mergeQuery.trim().length >= 2
    ? allItems.filter(o => o.id !== item.id && !o.duplicate_of && o.org_id === item.org_id &&
        o.title.toLowerCase().includes(mergeQuery.trim().toLowerCase())).slice(0, 5)
    : [];

  return (
    <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:20, minWidth:340, maxWidth:420 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <Badge tone={item.type === 'bug' ? 'warn' : 'accent'}>{item.type}</Badge>
          <Badge tone={FB_STATUS_TONE[item.status]}>{FB_STATUS_LABEL[item.status]}</Badge>
          <div style={{ fontSize:12, color:C.muted, marginTop:6 }}>{item.organizations?.name || 'Unknown org'} · {item.profiles?.name || 'Unknown user'} · {new Date(item.created_at).toLocaleString()}</div>
          {item.area && <div style={{ fontSize:12, color:C.muted }}>Area: {item.area}</div>}
        </div>
        <button onClick={onClose} style={{ background:'none', border:'none', color:C.muted, cursor:'pointer', fontSize:18, lineHeight:1 }}>×</button>
      </div>

      <div style={{ marginTop:14, fontSize:14, fontWeight:700, color:C.text }}>{item.title}</div>
      <div style={{ marginTop:6, fontSize:13, lineHeight:1.6, whiteSpace:'pre-wrap', color:C.muted }}>{item.message}</div>

      {item.duplicate_of && (
        <div style={{ marginTop:10, fontSize:12, color:'#fbbf24' }}>
          Merged into another item. This row is kept for its own history.
        </div>
      )}

      <div style={{ marginTop:18, borderTop:`1px solid ${C.border}`, paddingTop:14 }}>
        <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:8 }}>Status</div>
        <select value={nextStatus} onChange={e => setNextStatus(e.target.value)}
          style={{ width:'100%', padding:'7px 10px', borderRadius:7, border:`1px solid ${C.border}`, background:C.bg, color:C.text, fontSize:13, marginBottom:8 }}>
          <option value={item.status}>{FB_STATUS_LABEL[item.status]} (current)</option>
          {(FB_NEXT_STATES[item.status] || []).map(s => <option key={s} value={s}>{FB_STATUS_LABEL[s]}</option>)}
        </select>

        <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:2 }}>Official response</div>
        <div style={{ fontSize:11, color:C.muted, marginBottom:8 }}>
          Visible to everyone at {item.organizations?.name || 'this org'} — not just the submitter.
        </div>
        <textarea value={responseText} onChange={e => setResponseText(e.target.value)}
          placeholder="e.g. 'We're not building custom invoice templates because it breaks automated accounting. Use the standard editor instead.'"
          rows={4}
          style={{ width:'100%', padding:'8px 10px', borderRadius:7, border:`1px solid ${C.border}`, background:C.bg, color:C.text, fontSize:13, resize:'vertical', boxSizing:'border-box', fontFamily:'inherit' }} />
        {needsResponseWarning && (
          <div style={{ fontSize:11, color:'#fbbf24', marginTop:6 }}>
            {FB_STATUS_LABEL[nextStatus]} usually reads better with a response explaining why — not required, just a nudge.
          </div>
        )}
        <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:8 }}>
          <button onClick={save} disabled={saving || (!statusChanged && !responseChanged)}
            style={{ padding:'7px 14px', borderRadius:7, border:'none', background:C.accent, color:'#031420', fontWeight:700, fontSize:12,
              cursor: saving ? 'default' : 'pointer', opacity: (saving || (!statusChanged && !responseChanged)) ? 0.5 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          {saved && <span style={{ fontSize:12, color:'#4ade80' }}>Saved ✓</span>}
          {saveErr && <span style={{ fontSize:12, color:'#f87171' }}>{saveErr}</span>}
        </div>
        {item.admin_response && item.responded_at && (
          <div style={{ fontSize:11, color:C.muted, marginTop:8 }}>Last responded {new Date(item.responded_at).toLocaleString()}</div>
        )}
      </div>

      <div style={{ marginTop:18, borderTop:`1px solid ${C.border}`, paddingTop:14 }}>
        <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:8 }}>Internal note <span style={{ color:C.muted, fontWeight:400 }}>— TimeKeep HQ only, never visible to the org</span></div>
        {notes.map(n => (
          <div key={n.id} style={{ fontSize:12, color:C.muted, marginBottom:8, paddingLeft:8, borderLeft:`2px solid ${C.border}` }}>
            <div style={{ color:C.text }}>{n.note}</div>
            <div style={{ fontSize:10, marginTop:2 }}>{n.profiles?.name || 'Someone'} · {new Date(n.created_at).toLocaleDateString()}</div>
          </div>
        ))}
        <div style={{ display:'flex', gap:8 }}>
          <input value={internalNote} onChange={e => setInternalNote(e.target.value)} placeholder="e.g. Revisit if 3 more people ask"
            onKeyDown={e => e.key==='Enter' && postNote()}
            style={{ flex:1, padding:'7px 10px', borderRadius:7, border:`1px solid ${C.border}`, background:C.bg, color:C.text, fontSize:12 }} />
          <button onClick={postNote} disabled={!internalNote.trim()}
            style={{ padding:'7px 12px', borderRadius:7, border:`1px solid ${C.border}`, background:'transparent', color:C.text, fontSize:12, cursor:'pointer' }}>Add</button>
        </div>
      </div>

      <div style={{ marginTop:18, borderTop:`1px solid ${C.border}`, paddingTop:14 }}>
        <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:8 }}>Comments ({comments.length})</div>
        {comments.map(c => (
          <div key={c.id} style={{ fontSize:12, marginBottom:8, display:'flex', justifyContent:'space-between', gap:8 }}>
            <div>
              <div style={{ color:C.muted, marginBottom:2 }}>{c.profiles?.name || 'Someone'} · {new Date(c.created_at).toLocaleDateString()}</div>
              <div style={{ color:C.text }}>{c.body}</div>
            </div>
            <button onClick={() => onDeleteComment(c.id, () => setComments(cs => cs.filter(x => x.id !== c.id)))}
              title="Remove this comment (moderation)"
              style={{ background:'none', border:'none', color:C.muted, cursor:'pointer', fontSize:14, flexShrink:0 }}>🗑</button>
          </div>
        ))}
        {comments.length === 0 && <div style={{ fontSize:12, color:C.muted }}>No comments.</div>}
      </div>

      {!item.duplicate_of && (
        <div style={{ marginTop:18, borderTop:`1px solid ${C.border}`, paddingTop:14 }}>
          <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:8 }}>Merge as duplicate of…</div>
          <input value={mergeQuery} onChange={e => setMergeQuery(e.target.value)} placeholder="Search this org's other items by title"
            style={{ width:'100%', padding:'7px 10px', borderRadius:7, border:`1px solid ${C.border}`, background:C.bg, color:C.text, fontSize:12, boxSizing:'border-box' }} />
          {mergeCandidates.map(c => (
            <div key={c.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 0', fontSize:12 }}>
              <span style={{ flex:1, color:C.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.title}</span>
              <button onClick={() => onMerge(item.id, c.id)}
                style={{ padding:'4px 10px', borderRadius:6, border:`1px solid ${C.accent}`, background:'transparent', color:C.accent, fontSize:11, fontWeight:600, cursor:'pointer' }}>
                Merge into this
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FeedbackTab() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [areaFilter, setAreaFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState(null);

  const load = () => {
    supabase.from('feedback')
      .select('id, org_id, user_id, type, title, message, area, status, duplicate_of, admin_response, responded_at, created_at, organizations(name), profiles(name)')
      .order('created_at', { ascending: false })
      .limit(500)
      .then(({ data, error }) => {
        if (error) setError(error.message);
        else setRows(data || []);
      });
  };

  useEffect(load, []);

  // Status + response are saved together. The DB trigger is the real
  // enforcement of the transition matrix -- an invalid jump throws here and
  // that error is surfaced directly rather than silently swallowed.
  const postResponse = async (id, newStatus, text) => {
    const current = rows.find(r => r.id === id);
    const patch = { admin_response: text || null };
    if (newStatus !== current.status) patch.status = newStatus;
    const { error } = await supabase.from('feedback').update(patch).eq('id', id);
    if (error) return error.message;
    const { data: fresh } = await supabase.from('feedback')
      .select('id, org_id, user_id, type, title, message, area, status, duplicate_of, admin_response, responded_at, created_at, organizations(name), profiles(name)')
      .eq('id', id).single();
    if (fresh) setRows(prev => prev.map(r => r.id === id ? fresh : r));
    return null;
  };

  const deleteComment = async (commentId, onDone) => {
    const { error } = await supabase.rpc('delete_feedback_comment', { p_comment_id: commentId });
    if (!error) onDone();
  };

  const mergeItems = async (duplicateId, canonicalId) => {
    const { error } = await supabase.rpc('merge_duplicate_feedback', { p_duplicate_id: duplicateId, p_canonical_id: canonicalId });
    if (!error) { load(); setSelectedId(canonicalId); }
  };

  const resendDigest = async () => {
    setResending(true);
    setResendMsg(null);
    const { data, error } = await supabase.functions.invoke('feedback-digest', {});
    setResending(false);
    if (error) setResendMsg('Failed to send — check function logs.');
    else if (data?.sent) setResendMsg(`Sent to ${data.recipients} recipient(s).`);
    else setResendMsg(data?.reason === 'no_feedback' ? 'No feedback in the last 7 days — nothing sent.' : 'Nothing sent (no platform admins configured?).');
  };

  if (error) return <div style={{ color:'#f87171' }}>Failed to load: {error}</div>;
  if (!rows) return <div style={{ color:C.muted }}>Loading…</div>;

  const now = Date.now();
  const last7d = rows.filter(r => now - new Date(r.created_at).getTime() < 7 * 86400000).length;
  const last30d = rows.filter(r => now - new Date(r.created_at).getTime() < 30 * 86400000).length;
  const newCount = rows.filter(r => r.status === 'new' && !r.duplicate_of).length;

  const filtered = rows.filter(r => {
    if (r.duplicate_of) return false; // merged-away rows are only reachable via the canonical item
    if (typeFilter !== 'all' && r.type !== typeFilter) return false;
    if (areaFilter !== 'all' && r.area !== areaFilter) return false;
    if (search && !`${r.organizations?.name || ''} ${r.title} ${r.message} ${r.area || ''}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const selected = rows.find(r => r.id === selectedId);

  return (
    <div>
      <div style={{ display:'flex', gap:16, flexWrap:'wrap', marginBottom:20, alignItems:'flex-end' }}>
        <StatTile label="New" value={newCount} />
        <StatTile label="Last 7 Days" value={last7d} />
        <StatTile label="Last 30 Days" value={last30d} />
        <StatTile label="Total" value={rows.length} />
        <div style={{ marginLeft:'auto', display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4 }}>
          <button onClick={resendDigest} disabled={resending}
            style={{ padding:'8px 16px', borderRadius:7, border:`1px solid ${C.border}`, background:'transparent', color:C.text, fontSize:13, cursor: resending ? 'default' : 'pointer', opacity: resending ? 0.6 : 1 }}>
            {resending ? 'Sending…' : 'Resend Digest Now'}
          </button>
          {resendMsg && <div style={{ fontSize:12, color:C.muted }}>{resendMsg}</div>}
        </div>
      </div>

      <div style={{ display:'flex', gap:20, alignItems:'flex-start' }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
            <input placeholder="Search org, title, message, area…" value={search} onChange={e => setSearch(e.target.value)}
              style={{ padding:'7px 10px', borderRadius:7, border:`1px solid ${C.border}`, background:C.surface, color:C.text, fontSize:13, minWidth:220 }} />
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
              style={{ padding:'7px 10px', borderRadius:7, border:`1px solid ${C.border}`, background:C.surface, color:C.text, fontSize:13 }}>
              <option value="all">All Types</option>
              {FEEDBACK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={areaFilter} onChange={e => setAreaFilter(e.target.value)}
              style={{ padding:'7px 10px', borderRadius:7, border:`1px solid ${C.border}`, background:C.surface, color:C.text, fontSize:13 }}>
              <option value="all">All Areas</option>
              {FEEDBACK_AREAS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <span style={{ fontSize:12, color:C.muted, marginLeft:'auto' }}>{filtered.length} of {rows.length}</span>
          </div>
          <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ textAlign:'left', color:C.muted, fontSize:11, textTransform:'uppercase' }}>
                  <th style={{ padding:'10px 14px' }}>Date</th>
                  <th style={{ padding:'10px 14px' }}>Org</th>
                  <th style={{ padding:'10px 14px' }}>Type</th>
                  <th style={{ padding:'10px 14px' }}>Title</th>
                  <th style={{ padding:'10px 14px' }}>Area</th>
                  <th style={{ padding:'10px 14px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id} onClick={() => setSelectedId(r.id)}
                    style={{ borderTop:`1px solid ${C.border}`, cursor:'pointer', background: selectedId === r.id ? `${C.accent}11` : 'transparent' }}>
                    <td style={{ padding:'10px 14px', color:C.muted, whiteSpace:'nowrap' }}>{new Date(r.created_at).toLocaleDateString()}</td>
                    <td style={{ padding:'10px 14px' }}>{r.organizations?.name || '—'}</td>
                    <td style={{ padding:'10px 14px' }}><Badge tone={r.type === 'bug' ? 'warn' : 'accent'}>{r.type}</Badge></td>
                    <td style={{ padding:'10px 14px', maxWidth:280, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.title}</td>
                    <td style={{ padding:'10px 14px', color:C.muted, whiteSpace:'nowrap' }}>{r.area || '—'}</td>
                    <td style={{ padding:'10px 14px' }}><Badge tone={FB_STATUS_TONE[r.status]}>{FB_STATUS_LABEL[r.status]}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {selected && (
          <FeedbackDetailPanel item={selected} allItems={rows} onClose={() => setSelectedId(null)}
            onRespond={postResponse} onDeleteComment={deleteComment} onMerge={mergeItems} />
        )}
      </div>
    </div>
  );
}

function ToolsTab() {
  const [email, setEmail] = useState('');
  const [result, setResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  const search = async () => {
    if (!email.trim()) return;
    setSearching(true);
    setError(null);
    setResult(null);
    const { data, error } = await supabase.functions.invoke('platform-user-lookup', { body: { email: email.trim() } });
    setSearching(false);
    if (error || !data?.ok) setError(data?.error || 'User not found');
    else setResult(data);
  };

  return (
    <div>
      <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:20, maxWidth:480 }}>
        <div style={{ fontWeight:700, marginBottom:10 }}>User Lookup</div>
        <div style={{ display:'flex', gap:8 }}>
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="user@example.com"
            onKeyDown={e => e.key === 'Enter' && search()}
            style={{ flex:1, padding:'7px 10px', borderRadius:7, border:`1px solid ${C.border}`, background:C.bg, color:C.text, fontSize:13 }} />
          <button onClick={search} disabled={searching}
            style={{ padding:'7px 16px', borderRadius:7, border:'none', background:C.accent, color:'#031420', fontWeight:700, fontSize:13, cursor: searching ? 'default' : 'pointer', opacity: searching ? 0.6 : 1 }}>
            {searching ? '…' : 'Search'}
          </button>
        </div>
        {error && <div style={{ color:'#f87171', fontSize:13, marginTop:10 }}>{error}</div>}
        {result && (
          <div style={{ marginTop:16, fontSize:13 }}>
            <div style={{ color:C.muted }}>{result.user.email} · joined {new Date(result.user.created_at).toLocaleDateString()}</div>
            <div style={{ color:C.muted, marginTop:4 }}>Last time entry: {result.last_entry_date || 'none'}</div>
            <div style={{ marginTop:12, display:'flex', flexDirection:'column', gap:8 }}>
              {(result.memberships || []).length === 0 && <div style={{ color:C.muted }}>No org memberships.</div>}
              {(result.memberships || []).map((m, i) => (
                <div key={i} style={{ borderTop:`1px solid ${C.border}`, paddingTop:8 }}>
                  <div style={{ fontWeight:600 }}>{m.organizations?.name || m.org_id}</div>
                  <div style={{ color:C.muted, fontSize:12 }}>{m.role} · {m.seat_type}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/*
        NARROWED 2026-08-03: the original blanket exclusion here predates
        GuidePoint/TimeKeep separation, when this console's only admin was also
        the sole customer. It's now split into two decisions:
          - Still excluded: HQ directly assuming a specific customer user's
            literal identity/session from this tool. Not built, not wanted here
            without its own separate threat-modeling conversation.
          - Now deliberately built (Organizations tab, "Enter Support Session"):
            an HQ admin gaining a real, audited, temporary org_members row in a
            test/internal org (organizations.is_test = true only — never a real
            customer org by default). Once inside, they're a normal org admin
            and can use that org's own existing "View As" feature on its team
            members — same mechanism a real org admin already has, not a new
            cross-tenant identity-assumption path. See migration
            20260803_platform_admin_support_access.sql.
      */}

      <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:20, marginTop:20, maxWidth:480 }}>
        <div style={{ fontWeight:700, marginBottom:10 }}>Supabase Dashboard</div>
        <div style={{ display:'flex', flexDirection:'column', gap:8, fontSize:13 }}>
          <a href="https://supabase.com/dashboard/project/jydqptjofnyfqanfnhxm/logs/edge-functions-logs" target="_blank" rel="noreferrer" style={{ color:C.accent }}>Edge Function Logs →</a>
          <a href="https://supabase.com/dashboard/project/jydqptjofnyfqanfnhxm/advisors/security" target="_blank" rel="noreferrer" style={{ color:C.accent }}>Security Advisors →</a>
          <a href="https://supabase.com/dashboard/project/jydqptjofnyfqanfnhxm/database/cron-jobs" target="_blank" rel="noreferrer" style={{ color:C.accent }}>Cron Jobs →</a>
        </div>
      </div>
    </div>
  );
}

function OverviewTab() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    supabase.rpc('platform_admin_org_summary').then(({ data, error }) => {
      if (error) setError(error.message);
      else setRows(data || []);
    });
  }, []);

  if (error) return <div style={{ color:'#f87171' }}>Failed to load: {error}</div>;
  if (!rows) return <div style={{ color:C.muted }}>Loading…</div>;

  const real = rows.filter(r => !r.is_test);
  const totalOrgs = real.length;
  const orgAccounts = real.filter(r => r.account_type === 'organization').length;
  const individualAccounts = real.filter(r => r.account_type === 'individual').length;
  const paidTotal = real.reduce((sum, r) => sum + Number(r.paid_total || 0), 0);
  const testCount = rows.length - real.length;

  return (
    <div>
      <div style={{ display:'flex', gap:16, flexWrap:'wrap', marginBottom:24 }}>
        <StatTile label="Real Orgs" value={totalOrgs} />
        <StatTile label="Organization Accounts" value={orgAccounts} />
        <StatTile label="Individual Accounts" value={individualAccounts} />
        <StatTile label="Paid Across Tenants" value={`$${paidTotal.toLocaleString()}`} />
        <StatTile label="Test/Demo Orgs (hidden above)" value={testCount} />
      </div>
    </div>
  );
}

export default function PlatformAdmin({ session, profile, onSignOut }) {
  const [nav, setNav] = useState('overview');

  return (
    <div style={{ minHeight:'100vh', background:C.bg, color:C.text, fontFamily:'Inter,sans-serif' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 24px', borderBottom:`1px solid ${C.border}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:24 }}>
          <div style={{ fontWeight:700, fontSize:16 }}>
            TimeKeep <span style={{ color:C.accent }}>HQ</span>
          </div>
          <nav style={{ display:'flex', gap:4 }}>
            {NAV_ITEMS.map(item => (
              <button key={item.key} onClick={() => setNav(item.key)}
                style={{
                  padding:'6px 14px', borderRadius:7, border:'none', cursor:'pointer',
                  fontSize:13, fontWeight:600,
                  background: nav === item.key ? `${C.accent}22` : 'transparent',
                  color: nav === item.key ? C.accent : C.muted,
                }}>
                {item.label}
              </button>
            ))}
          </nav>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:13, color:C.muted }}>{session?.user?.email}</span>
          <button onClick={onSignOut}
            style={{ padding:'6px 14px', borderRadius:7, border:`1px solid ${C.border}`, background:'transparent', color:C.text, fontSize:13, cursor:'pointer' }}>
            Sign Out
          </button>
        </div>
      </div>
      <div style={{ padding:24 }}>
        {nav === 'overview' && <OverviewTab />}
        {nav === 'orgs' && <OrganizationsTab />}
        {nav === 'sandbox' && <SandboxTab />}
        {nav === 'feedback' && <FeedbackTab />}
        {nav === 'audit' && <AuditLogTab />}
        {nav === 'tools' && <ToolsTab />}
      </div>
    </div>
  );
}
